/**
 * Talks to the 1QA Recorder extension through window.postMessage. The extension side (and the
 * message format) is apps/recorder/src/core/bridge.ts; keep the two in sync. The extension only
 * answers on pages carrying <meta name="1qa-app"> (index.html).
 */
const PAGE_SOURCE = '1qa-app';
const EXT_SOURCE = '1qa-recorder';

/** First recorder version that can be started from the web app. */
export const RECORDER_MIN_VERSION = '0.4.0';
/**
 * First recorder version that records performance scripts on its own (started from "New script", saved to the Scripts).
 * 0.6.1: 0.6.0 asked for the debugger permission in its popup, which Chrome closed with the prompt, so it never started.
 * 0.6.2: 0.6.1 lost the recorded requests when Chrome stopped its idle service worker during the review.
 */
export const RECORDER_PERF_MIN_VERSION = '0.6.2';

export type RecorderStartCode = 'NOT_CONNECTED' | 'BUSY' | 'UNSAVED' | 'SIGNED_OUT' | 'TIMEOUT';
export interface RecorderStartResult {
  ok: boolean;
  code?: RecorderStartCode;
  error?: string;
  /** The 1QA address the recorder should connect to (NOT_CONNECTED). */
  address?: string;
}

interface ExtMessage {
  source: typeof EXT_SOURCE;
  type: 'pong' | 'start-result' | 'test-saved' | 'script-saved' | 'recording-stopped';
  version?: string;
  versionName?: string;
  requestId?: string;
  projectId?: string;
  testId?: string;
  scriptId?: string;
  name?: string;
}

/** An installed recorder: `version` is "0.4.0.12", `versionName` "0.4.0 build 12" (each build has its own number). */
export interface RecorderInfo {
  version: string;
  versionName: string;
}
const infoOf = (m: ExtMessage): RecorderInfo | null => (typeof m.version === 'string' ? { version: m.version, versionName: m.versionName ?? m.version } : null);

const isExtMessage = (e: MessageEvent): e is MessageEvent<ExtMessage> =>
  e.source === window && e.origin === window.location.origin && (e.data as ExtMessage | null)?.source === EXT_SOURCE;

// The extension announces itself when the page loads; remember it so a later check is instant.
let known: RecorderInfo | null = null;
if (typeof window !== 'undefined') {
  window.addEventListener('message', (e) => {
    if (isExtMessage(e) && e.data.type === 'pong') known = infoOf(e.data) ?? known;
  });
}

/** True when `version` (x.y.z) is at least `min`. */
/** Latest recorder build, written by apps/recorder/build.mjs and served next to the zip. */
export interface RecorderBuild {
  version: string;
  versionName: string;
  build: number;
  builtAt: string;
  commit: string | null;
}
export const RECORDER_BUILD_INFO = '/downloads/1qa-recorder.json';

/** The latest build offered for download, or null when unknown (e.g. the Vite dev server has no /downloads). */
export async function fetchLatestRecorder(): Promise<RecorderBuild | null> {
  try {
    const res = await fetch(RECORDER_BUILD_INFO, { cache: 'no-cache' });
    if (!res.ok || !res.headers.get('content-type')?.includes('json')) return null;
    const data = (await res.json()) as Partial<RecorderBuild>;
    return typeof data.version === 'string' ? (data as RecorderBuild) : null;
  } catch {
    return null;
  }
}

/** Saved as e.g. 1qa-recorder-0.5.1.7212.zip, so downloads of different builds are told apart (like the agents). */
export const recorderFileName = (build: { version: string } | null | undefined) => (build ? `1qa-recorder-${build.version}.zip` : '1qa-recorder.zip');

export function versionAtLeast(version: string, min: string): boolean {
  const a = version.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const b = min.split('.').map((n) => Number.parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d > 0;
  }
  return true;
}

/**
 * Installed recorder, or null when it is not installed (or the page was opened before it was
 * installed - the extension only reaches pages loaded after installing). Versions before 0.4 never answer.
 */
export function detectRecorder(timeoutMs = 700): Promise<RecorderInfo | null> {
  if (known) return Promise.resolve(known);
  return new Promise((resolve) => {
    const onMessage = (e: MessageEvent) => {
      if (!isExtMessage(e) || e.data.type !== 'pong') return;
      cleanup();
      resolve(infoOf(e.data));
    };
    const timer = window.setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);
    const cleanup = () => {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
    };
    window.addEventListener('message', onMessage);
    window.postMessage({ source: PAGE_SOURCE, type: 'ping' }, window.location.origin);
  });
}

/**
 * Asks the recorder to open a new window and record into that project. `url: null` starts on a blank
 * page (what "New test" does): the user types the address they want to test. `perf`: record a performance script of a
 * performance project (recorder 0.6.0+; older ones would record a functional test).
 */
export function startRecorder(input: { orgId: string; projectId: string; url: string | null; perf?: boolean }, timeoutMs = 15_000): Promise<RecorderStartResult> {
  const requestId = Math.random().toString(36).slice(2, 12);
  return new Promise((resolve) => {
    const onMessage = (e: MessageEvent) => {
      if (!isExtMessage(e) || e.data.type !== 'start-result' || e.data.requestId !== requestId) return;
      cleanup();
      const { ok, code, error, address } = e.data as unknown as RecorderStartResult;
      resolve({ ok: Boolean(ok), code, error, address });
    };
    const timer = window.setTimeout(() => {
      cleanup();
      resolve({ ok: false, code: 'TIMEOUT' });
    }, timeoutMs);
    const cleanup = () => {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
    };
    window.addEventListener('message', onMessage);
    window.postMessage({ source: PAGE_SOURCE, type: 'start', requestId, ...input }, window.location.origin);
  });
}

/** A test saved from the recorder (it calls the API itself, so the app hears about it here). */
export interface RecorderSavedTest {
  projectId: string;
  testId: string;
  name: string;
}

/** Calls `onSaved` whenever the recorder saves a test; returns the unsubscribe function. */
export function onRecorderTestSaved(onSaved: (saved: RecorderSavedTest) => void): () => void {
  const listener = (e: MessageEvent) => {
    if (!isExtMessage(e) || e.data.type !== 'test-saved') return;
    const { projectId, testId, name } = e.data;
    if (typeof projectId === 'string' && typeof testId === 'string') onSaved({ projectId, testId, name: typeof name === 'string' ? name : 'Recorded test' });
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
}

/** A performance script saved from the recorder to a performance project's library. */
export interface RecorderSavedScript {
  projectId: string;
  scriptId: string;
  name: string;
}

/** Calls `onSaved` whenever the recorder saves a performance script; returns the unsubscribe function. */
export function onRecorderScriptSaved(onSaved: (saved: RecorderSavedScript) => void): () => void {
  const listener = (e: MessageEvent) => {
    if (!isExtMessage(e) || e.data.type !== 'script-saved') return;
    const { projectId, scriptId, name } = e.data;
    if (typeof projectId === 'string' && typeof scriptId === 'string') onSaved({ projectId, scriptId, name: typeof name === 'string' ? name : 'Recorded script' });
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
}

/** Calls `onStopped` when a recording stopped but the browser did not open the recorder's review screen. */
export function onRecorderStopped(onStopped: () => void): () => void {
  const listener = (e: MessageEvent) => {
    if (isExtMessage(e) && e.data.type === 'recording-stopped') onStopped();
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
}
